import { useMutation, useQueryClient } from '@tanstack/react-query';
import { add } from 'date-fns';

import { QueryKey } from ':cloud/queries/types';
import { APIClientBase } from ':cloud/state/Api';
import { RotatePlatformTokenResponse } from ':cloud/types/service_proto';

const errorMsg = 'An error occurred rotating the Client API Key. Please retry';

export const expiresTimes = ['now', '1 hour', '24 hours', '3 days', '7 days', '14 days'] as const;

export type ExpiresTime = (typeof expiresTimes)[number];

const expiresTimesOffets = {
  now: null,
  '1 hour': { hours: 1 },
  '24 hours': { hours: 24 },
  '3 days': { days: 3 },
  '7 days': { days: 7 },
  '14 days': { days: 14 },
};

type RotateTokenProps = {
  organizationId: string;
  projectId: string;
  tokenId: string;
  expiresTime: ExpiresTime;
};

function addOffsetToUTCDate(expiresTime: ExpiresTime) {
  if (expiresTime !== 'now') {
    return add(new Date(), expiresTimesOffets[expiresTime]);
  }

  return new Date();
}

async function rotateToken({
  organizationId,
  projectId,
  tokenId,
  expiresTime,
}: RotateTokenProps): Promise<RotatePlatformTokenResponse | undefined> {
  const path = `/apikeys/v1/organizations/${organizationId}/projects/${projectId}/tokens:rotate`;
  const body = {
    tokenId,
    expiresAt: addOffsetToUTCDate(expiresTime),
  };
  const { data } = await APIClientBase.post<RotatePlatformTokenResponse>(path, body);
  return data;
}

type RotateTokenMutate = {
  tokenId: string;
  expiresTime: ExpiresTime;
};

export function useRotateToken(organizationId, projectId, onSuccess?: (clientId: string) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tokenId, expiresTime }: RotateTokenMutate) =>
      rotateToken({ organizationId, projectId, tokenId, expiresTime }),
    onSuccess: async (rotatedToken) => {
      if (!rotatedToken) return;
      await queryClient.invalidateQueries([QueryKey.tokens, organizationId]);
      onSuccess?.(rotatedToken.id);
    },
    meta: {
      errorMsg,
    },
  });
}
